/* eslint-disable camelcase */
import config from '../config';
import {
  getContestAttributionProperties,
  getPendingContestCampaignContext,
  getUtmAttributionFromSearch,
} from './contestCampaignContext';
import { trackImmediately } from './mixpanel';
import {
  markOnboardingStepCompleted,
  resetOnboardingAnalyticsState,
  updateOnboardingAnalyticsState,
} from './onboardingAnalyticsState';

export const ONBOARDING_SIGNUP_EVENTS = {
  viewed: 'onboarding_signup_viewed',
  started: 'onboarding_signup_started',
  emailEntered: 'onboarding_signup_email_entered',
  succeeded: 'onboarding_signup_succeeded',
  failed: 'onboarding_signup_failed',
  oauthTapped: 'onboarding_signup_oauth_tapped',
  guestContinueTapped: 'onboarding_guest_continue_tapped',
};

export const SIGNUP_METHOD_EMAIL = 'email';
const getSignupCampaignAttribution = () => {
  const pending = getPendingContestCampaignContext();
  const current = typeof window === 'undefined'
    ? {}
    : getUtmAttributionFromSearch(window.location.search);

  return getContestAttributionProperties({
    utmSource: current.utmSource || pending?.utmSource || null,
    utmMedium: current.utmMedium || pending?.utmMedium || null,
    utmCampaign: current.utmCampaign || pending?.utmCampaign || null,
    utmContent: current.utmContent || pending?.utmContent || null,
    utmTerm: current.utmTerm || pending?.utmTerm || null,
  });
};

export const trackOnboardingSignup = (eventName, properties = {}) => {
  if (eventName === ONBOARDING_SIGNUP_EVENTS.viewed) {
    resetOnboardingAnalyticsState();
  }

  if (eventName === ONBOARDING_SIGNUP_EVENTS.oauthTapped) {
    updateOnboardingAnalyticsState({ signup_method: properties.provider || null });
  }

  if (eventName === ONBOARDING_SIGNUP_EVENTS.succeeded) {
    updateOnboardingAnalyticsState({
      signup_method: properties.signup_method || SIGNUP_METHOD_EMAIL,
      is_edu_email: properties.is_edu_email,
    });
    markOnboardingStepCompleted('signup');
  }

  return trackImmediately(eventName, {
    platform: 'web',
    ...getSignupCampaignAttribution(),
    ...properties,
  });
};

export const getEmailTrackingProperties = (email = '') => {
  const normalizedEmail = `${email}`.trim().toLowerCase();
  const atIndex = normalizedEmail.lastIndexOf('@');
  const emailDomain = atIndex >= 0 ? normalizedEmail.slice(atIndex + 1) : '';

  return {
    is_edu_email: emailDomain === 'edu' || emailDomain.endsWith('.edu'),
  };
};

export const getSignupSourceScreen = (location) => {
  const stateSource = location?.state?.sourceScreen
    || location?.state?.source_screen
    || location?.state?.redirectPath;

  if (stateSource) return stateSource;

  if (typeof document === 'undefined' || !document.referrer) return 'direct';

  try {
    const referrer = new URL(document.referrer);

    if (typeof window !== 'undefined' && referrer.origin === window.location.origin) {
      return `${referrer.pathname}${referrer.search}` || '/';
    }

    return referrer.hostname || 'external';
  } catch {
    return 'external';
  }
};

export const getSignupViewProperties = ({ darkMode, location }) => ({
  theme: darkMode ? 'dark' : 'light',
  source_screen: getSignupSourceScreen(location),
  app_version: config.version,
});

export const classifySignupError = (error) => {
  const status = error?.status || error?.response?.status;
  let rawMessage = '';

  if (typeof error === 'string') {
    rawMessage = error;
  } else {
    rawMessage = error?.data?.message
      || error?.response?.data?.message
      || error?.message
      || '';
  }

  const message = `${rawMessage}`.toLowerCase();

  if (
    message.includes('email')
    && (
      message.includes('taken')
      || message.includes('already')
      || message.includes('exists')
      || message.includes('in use')
    )
  ) {
    return 'email_taken';
  }

  if (
    message.includes('password')
    && (
      message.includes('invalid')
      || message.includes('weak')
      || message.includes('short')
      || message.includes('minimum')
      || message.includes('at least')
    )
  ) {
    return 'invalid_password';
  }

  if (
    status === 0
    || status >= 500
    || message.includes('network')
    || message.includes('timeout')
    || message.includes('failed to fetch')
    || message.includes('connection')
  ) {
    return 'network_error';
  }

  return 'unknown_error';
};
